import { DetailPageLayout } from '@app/DetailPageLayout';
import { Form, Grid, IOption, IconName, Scrollbar, convertISOStringToLocalDateString, useForm } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { getAccessMode } from '@common/detailPage.hooks';
import { useNavigate } from '@routes/routes.hooks';
import { useFetchAllPermittedRiskCategories } from '@riskCategory/riskCategory.queries';
import usePrompt from '@common/prompt.hooks';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { useMyUserProfile } from '@config/config.hooks';
import { ERoleId } from '@role/role.constants';
import { ECompanyCodeConst } from '@company/companyType/companyType.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { UseQueryResult } from '@tanstack/react-query';
import {
  useFetchProjectRequestCompanies,
  useFetchProjectRequestDefaultCompanies,
  useFetchProjectRequestDetails,
  useFetchProjectRequestTeams,
  useFetchProjectRequestUsers,
} from './projectRequest.queries';
import { ProjectRequestDetailForm } from './ProjectRequestDetailForm';
import { EProjectRequestStatus } from './projectRequest.constants';
import {
  EProjectRequestFormKeys,
  IProjectRequest,
  IProjectRequestCompanyFormState,
  IProjectRequestFormState,
  mockDataProjectRegion,
  mockDataProjectType,
} from './projectRequest.models';
import { IProjectRequestCompany } from './projectRequestCompany/projectRequestCompany.models';
import { IProjectRequestTeam } from './projectRequestTeam/projectRequestTeam.models';
import { IProjectRequestUser } from './projectRequestUser/projectRequestUser.models';
import { useProjectRequestDetailPage } from './projectRequestDetail.hooks';

interface IProjectRequestDetailPageProps {}

export const ProjectRequestDetailPage: React.FunctionComponent<IProjectRequestDetailPageProps> = ({}) => {
  const { id, mode } = getAccessMode();

  const [isBlockResetDefaultValues, setIsBlockResetDefaultValues] = React.useState(false);

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  const { navigate } = useNavigate();
  const {
    data: projectRequestCompanies,
    isLoading: isLoadingProjectRequestCompanies,
    isFetching: isFetchingProjectRequestCompanies,
    error: errorProjectRequestCompanies,
  } = useFetchProjectRequestCompanies(id, !isDirty);

  const {
    data: projectRequestTeams,
    isLoading: isLoadingProjectRequestTeams,
    isFetching: isFetchingProjectRequestTeams,
    error: errorProjectRequestTeams,
  } = useFetchProjectRequestTeams(id, !isDirty);

  const {
    data: projectRequestUsers,
    isLoading: isLoadingProjectRequestUsers,
    isFetching: isFetchingProjectRequestUsers,
    error: errorProjectRequestUsers,
  } = useFetchProjectRequestUsers(id, !isDirty);

  const {
    data: projectRequestDetail,
    isLoading: isLoadingProjectRequestDetail,
    isFetching: isFetchingProjectRequestDetail,
    error: errorGetProjectRequestDetail,
  } = useFetchProjectRequestDetails(id, !isDirty);

  React.useEffect(() => {
    if (
      isFetchingProjectRequestDetail ||
      isFetchingProjectRequestCompanies ||
      isFetchingProjectRequestTeams ||
      isFetchingProjectRequestUsers
    )
      return;
    setIsBlockResetDefaultValues(false);
  }, [
    isFetchingProjectRequestDetail,
    isFetchingProjectRequestCompanies,
    isFetchingProjectRequestTeams,
    isFetchingProjectRequestUsers,
  ]);

  const { data: riskCategoryOptions } = (
    useFetchAllPermittedRiskCategories({ isDirty }) as () => UseQueryResult<
      IOption<IRiskCategory>[],
      AxiosErrorResponse
    >
  )();
  const { data: defaultCompanies } = useFetchProjectRequestDefaultCompanies(!isDirty && mode === 'Create');

  const { updateFormCompanyValueWithRes } = useProjectRequestDetailPage(useFormMethods, riskCategoryOptions);

  const formCompanies = useFormMethods.watch(EProjectRequestFormKeys.COMPANIES);

  const myUserProfile = useMyUserProfile();

  const getDefaultFormValues = (
    modeLocal: DetailPageAccessMode,
    projectRequestDetailLocal?: IProjectRequest,
    projectRequestCompaniesLocal?: IProjectRequestCompany[],
    projectRequestTeamsLocal?: IProjectRequestTeam[],
    projectRequestUsersLocal?: IProjectRequestUser[],
    defaultCompaniesLocal?: IProjectRequestCompany[],
    formCompaniesLocal?: IProjectRequestCompanyFormState[],
    isBlockResetDefaultValuesLocal = false,
  ) => {
    if (isBlockResetDefaultValuesLocal) return useFormMethods.formState.defaultValues ?? {};
    let tempDefaultCompanies: IProjectRequestCompanyFormState[];

    if (mode === 'Create') {
      const formCompaniesLocalConverted = formCompaniesLocal?.map((c) => {
        let rolesTemp = { ...c.roles };

        Object.keys(c.roles ?? {}).map((key) => {
          rolesTemp[key] = rolesTemp[key] ?? [];
        });

        if (c.code === ECompanyCodeConst.VIEWERS) {
          rolesTemp = {
            ...rolesTemp,
            [ERoleId.VIEWER_NO_SUMMARY.toString()]: [
              { userProfile: myUserProfile, userProfileId: myUserProfile?.id as number },
            ],
          };
        }
        return { ...c, [EProjectRequestFormKeys.ROLES]: rolesTemp };
      });
      tempDefaultCompanies =
        formCompaniesLocalConverted && formCompaniesLocalConverted.length > 0
          ? formCompaniesLocalConverted
          : (defaultCompaniesLocal ?? []).map((c) => {
              return {
                ...c,
                maxRiskCategory: c.maxRiskCategory,
                isDefault: true,
              };
            });
    } else {
      tempDefaultCompanies = updateFormCompanyValueWithRes(
        projectRequestCompaniesLocal,
        projectRequestTeamsLocal,
        projectRequestUsersLocal,
      );
    }

    const typeOption = mockDataProjectType.find((type) => type.id === projectRequestDetailLocal?.typeId);
    const regionOption = mockDataProjectRegion.find((region) => region.id === projectRequestDetail?.regionId);

    const state: IProjectRequestFormState = {
      [EProjectRequestFormKeys.PROJECT_NO]: projectRequestDetailLocal?.projectNo ?? null,
      [EProjectRequestFormKeys.PROJECT_TYPE]: typeOption ?? null,
      [EProjectRequestFormKeys.PROJECT_REGION]: regionOption ?? null,
      [EProjectRequestFormKeys.PROJECT_TITLE]: projectRequestDetailLocal?.projectName ?? null,
      [EProjectRequestFormKeys.ADDRESS]: projectRequestDetailLocal?.address ?? null,
      [EProjectRequestFormKeys.DESCRIPTION]: projectRequestDetailLocal?.description ?? null,
      [EProjectRequestFormKeys.TEL]: projectRequestDetailLocal?.tel ?? null,
      [EProjectRequestFormKeys.FAX]: projectRequestDetailLocal?.fax ?? null,
      [EProjectRequestFormKeys.EMAIL]: projectRequestDetailLocal?.email ?? null,
      [EProjectRequestFormKeys.START_DATE]: projectRequestDetailLocal?.startDate
        ? convertISOStringToLocalDateString(projectRequestDetailLocal?.startDate ?? '')
        : '',
      [EProjectRequestFormKeys.END_DATE]: projectRequestDetailLocal?.finishDate
        ? convertISOStringToLocalDateString(projectRequestDetailLocal?.finishDate ?? '')
        : '',
      [EProjectRequestFormKeys.REMARK]: projectRequestDetailLocal?.remark ?? null,
      [EProjectRequestFormKeys.COMPANIES]: tempDefaultCompanies,
    };

    return state;
  };

  const isReadOnly = React.useMemo(() => {
    if (mode === 'Create' && !projectRequestDetail) return false;
    if (
      projectRequestDetail &&
      (projectRequestDetail.status === EProjectRequestStatus.DRAFT ||
        projectRequestDetail.status === EProjectRequestStatus.ENDORSED ||
        projectRequestDetail.status === EProjectRequestStatus.ENDORSED_AND_REVISED)
    )
      return false;
    return true;
  }, [mode, JSON.stringify(projectRequestDetail)]);

  const isLoading =
    isLoadingProjectRequestCompanies ||
    isLoadingProjectRequestTeams ||
    isLoadingProjectRequestUsers ||
    isLoadingProjectRequestDetail;
  const isFetching =
    isFetchingProjectRequestCompanies ||
    isFetchingProjectRequestTeams ||
    isFetchingProjectRequestUsers ||
    isFetchingProjectRequestDetail;
  const breadcrumbItems = [
    {
      text: 'Home',
      prefixIconName: 'House' as IconName,
      onClick: () => {
        navigate(routes.announcement.list.path);
      },
    },
    {
      text: 'Search Project Request',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(routes.projectRequest.path);
      },
    },
    {
      text: `${mode} Project Request`,
      prefixIconName: 'JournalText' as IconName,
      active: true,
    },
  ];

  return (
    <Scrollbar>
      <ApiErrorDisplay
        errors={[
          { error: errorGetProjectRequestDetail, genericError: 'Get project request failed' },
          { error: errorProjectRequestCompanies, genericError: 'Get project request companies failed' },
          { error: errorProjectRequestTeams, genericError: 'Get project request teams failed' },
          { error: errorProjectRequestUsers, genericError: 'Get project request users failed' },
        ]}
      />
      <DetailPageLayout
        errorGetDetail={errorGetProjectRequestDetail}
        isDrawer={false}
        data={
          mode === 'Create'
            ? defaultCompanies
            : projectRequestDetail && projectRequestCompanies && projectRequestTeams && projectRequestUsers
        }
        isRefetching={isFetching}
        isLoading={isLoading}
        title={`Project Request${
          mode !== 'Create' && projectRequestDetail?.projectNo ? ` - ${projectRequestDetail?.projectNo}` : ''
        }`}
        breadcrumb={breadcrumbItems}
        render={(DetailPageRightMenuOpener) => {
          return (
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <Form
                  enableUnsavedChangesPrompt
                  unsavedChangesPromptProps={{
                    usePrompt,
                  }}
                  defaultValues={{
                    callback: () => {
                      const state = getDefaultFormValues(
                        mode,
                        projectRequestDetail,
                        projectRequestCompanies,
                        projectRequestTeams,
                        projectRequestUsers,
                        defaultCompanies,
                        mode === 'Create' ? formCompanies : undefined,
                        isBlockResetDefaultValues,
                      );
                      return state;
                    },
                    deps: [
                      mode,
                      projectRequestDetail,
                      projectRequestCompanies,
                      projectRequestTeams,
                      projectRequestUsers,
                      defaultCompanies,
                      myUserProfile,
                      ...[mode === 'Create' ? formCompanies : undefined],
                      isBlockResetDefaultValues,
                    ],
                  }}
                  useFormMethods={useFormMethods}
                  render={(formFieldMethods) => {
                    return (
                      <ProjectRequestDetailForm
                        setIsBlockResetDefaultValues={setIsBlockResetDefaultValues}
                        onSuccessNavigate={(detailId: number) => {
                          setTimeout(() => {
                            navigate(routes.projectRequest.view.path, { id: detailId.toString() });
                          }, 100);
                        }}
                        useFormMethods={useFormMethods}
                        formFieldMethods={formFieldMethods}
                        isReadOnly={isReadOnly}
                        mode={mode}
                        id={id}
                        projectRequestDetail={projectRequestDetail}
                        projectRequestCompanies={projectRequestCompanies}
                        projectRequestTeams={projectRequestTeams}
                        projectRequestUsers={projectRequestUsers}
                        riskCategoryOptions={riskCategoryOptions}
                        defaultCompanies={defaultCompanies}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          );
        }}
      />
    </Scrollbar>
  );
};
